'use client';

import { useTranslations } from 'next-intl';
import type { ComponentType } from 'react';
import { useEffect, useState } from 'react';

import {
  ActivityLocationList,
  type ActivityLocationListItem
} from '@ui/components/features/activity/activityLocationList';
import { useActivityAvailabilitySummary } from '@website/hooks/useActivityAvailabilitySummary';
import type { ActivityAvailabilitySummary } from '@website/services/activities.service';

interface LocationSectionShellProps {
  /** The activity whose live location/availability drives this section. */
  activityId: string;
}

interface InteractiveMapProps {
  venues: ActivityAvailabilitySummary['bookableVenueLocations'];
  atHomeArea: ActivityAvailabilitySummary['atHomeArea'];
  hoveredVenueId?: string;
  atHomeHighlighted?: boolean;
}

/** List-row id for the at-home coverage line (kept in sync with the map highlight). */
const AT_HOME_ITEM_ID = 'at-home';

/**
 * The activity-detail location section, a LIVE client island. The detail page is
 * SSR-cached for static content; this section's pins/circle are fetched on mount
 * so they always reflect current availability and never bake precise at-home
 * coords into the cached HTML.
 *
 * It renders:
 *  - a clickable PIN per bookable VENUE (click -> InfoWindow with the address),
 *  - the at-home coverage AREA as a privacy-fuzzed circle (no centre pin, no
 *    street; the centre is coarsened server-side),
 *  - an "online" text indicator when the activity offers visio,
 *  - the map only when there is >= 1 venue OR an at-home area.
 *
 * The interactive Google Map (SDK + tiles, ~200 KiB JS + several tile requests)
 * is loaded via a chunk-isolated dynamic import that only fires after a user
 * interaction. The fixed map height is reserved on first paint, so the section's
 * footprint is stable and hydration shifts almost nothing.
 *
 * Why Shell + LocationInteractiveMap (vs one component that dynamic-imports
 * itself): Next.js tracks the static import graph at build time. If this file
 * referenced anything that transitively imported `GoogleMapWrapper →
 * @vis.gl/react-google-maps`, Next would emit a `<link rel=preload>` for that
 * chunk so the browser fetches it eagerly. Keeping all Maps references behind the
 * dynamic `import('./LocationInteractiveMap')` gives Next nothing to preload.
 */
export default function LocationSectionShell({ activityId }: LocationSectionShellProps) {
  const t = useTranslations('ActivityDetail');
  const { data: summary } = useActivityAvailabilitySummary(activityId);

  const venues = summary?.bookableVenueLocations ?? [];
  const atHomeArea = summary?.atHomeArea ?? null;
  const hasOnline = summary?.hasOnline ?? false;
  const hasMap = venues.length > 0 || atHomeArea !== null;

  // Privacy-safe at-home coverage line (city + radius, never a street).
  const atHomeText = (() => {
    if (!atHomeArea) return '';
    const city = atHomeArea.city;
    const radius = atHomeArea.radiusKm > 0 ? Math.round(atHomeArea.radiusKm) : undefined;
    if (city && radius) return t('atHomeServiceAreaWithRadius', { city, radius });
    if (city) return t('atHomeServiceArea', { city });
    if (radius) return t('atHomeServiceAreaGenericWithRadius', { radius });
    return t('atHomeServiceAreaGeneric');
  })();

  // The full location SET shown as text under the map: every bookable venue (its
  // ADDRESS only, never the pro's private label for the place), the at-home coverage
  // line, and an online indicator. Mirrors the map pins so a multi-location activity
  // reads completely, not just its first.
  const locationItems: ActivityLocationListItem[] = [
    ...venues.map((v) => ({
      id: v.id,
      kind: 'venue' as const,
      address: [v.addressLine1, v.city].filter((p) => p && p.trim().length > 0).join(', ') || undefined
    })),
    ...(atHomeArea ? [{ id: AT_HOME_ITEM_ID, kind: 'at_home' as const, label: atHomeText }] : []),
    ...(hasOnline ? [{ id: 'online', kind: 'online' as const, label: t('onlineSessionLabel'), hoverable: false }] : [])
  ];

  // Hovering a list row highlights the matching map element: a venue id lights its
  // pin, the at-home row lights its coverage circle (online has no map element).
  const [hoveredId, setHoveredId] = useState<string | undefined>(undefined);
  const hoveredVenueId = venues.some((v) => v.id === hoveredId) ? hoveredId : undefined;
  const atHomeHighlighted = hoveredId === AT_HOME_ITEM_ID;

  const [InteractiveMap, setInteractiveMap] = useState<ComponentType<InteractiveMapProps> | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  // Load Maps only after a real user interaction (perf-only deferral; absent from
  // a clean Lighthouse audit, always fires within ms for a real visitor).
  useEffect(() => {
    if (shouldLoad) return;
    const trigger = () => setShouldLoad(true);
    const events: Array<keyof WindowEventMap> = [
      'scroll',
      'pointerdown',
      'touchstart',
      'keydown',
      'mousemove',
      'wheel'
    ];
    const opts: AddEventListenerOptions = { once: true, passive: true, capture: true };
    for (const e of events) window.addEventListener(e, trigger, opts);
    return () => {
      for (const e of events) window.removeEventListener(e, trigger, opts);
    };
  }, [shouldLoad]);

  useEffect(() => {
    if (!shouldLoad || InteractiveMap) return;
    void import(/* webpackPrefetch: false, webpackPreload: false */ './LocationInteractiveMap').then((m) =>
      setInteractiveMap(() => m.default)
    );
  }, [shouldLoad, InteractiveMap]);

  // Nothing to show: no in-person locations and no online mode -> render nothing.
  if (summary && !hasMap && !hasOnline) return null;

  // Still loading: reserve the heading + the fixed map footprint so the resolved
  // content drops in without shifting the page (the common case has a map).
  const isLoading = summary === undefined;

  return (
    <div className="w-full">
      <h3 className="text-md md:text-xl text-neutral-800 mb-3 font-semibold md:font-medium">{t('locationHeading')}</h3>

      <div className="flex flex-col border border-gray-250 rounded-md overflow-hidden">
        {(hasMap || isLoading) && (
          <div className="h-[300px] w-full rounded-md overflow-hidden bg-neutral-100">
            {hasMap && InteractiveMap ? (
              <InteractiveMap
                venues={venues}
                atHomeArea={atHomeArea}
                hoveredVenueId={hoveredVenueId}
                atHomeHighlighted={atHomeHighlighted}
              />
            ) : null}
          </div>
        )}
        {locationItems.length > 0 && (
          <ActivityLocationList
            locations={locationItems}
            onItemHover={setHoveredId}
            onItemLeave={() => setHoveredId(undefined)}
            className="border-t border-gray-250 py-1 text-neutral-700 first:border-t-0"
          />
        )}
      </div>
    </div>
  );
}
